import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const q = (p: Record<string, unknown>) => new URLSearchParams(
  Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
).toString()

export interface AllInOneItem {
  id: number
  variant_id: number
  code: string
  item_type: string
  sessions_count?: number | null
  icon?: string | null
  title: string
  title_ar?: string | null
  title_ur?: string | null
  subtitle?: string | null
  subtitle_ar?: string | null
  subtitle_ur?: string | null
  sort_order?: number | null
  [k: string]: unknown
}

export interface AllInOneVariant {
  id: number
  bundle_id: number
  code: string
  has_licence?: boolean
  title: string
  title_ar?: string | null
  title_ur?: string | null
  description?: string | null
  description_ar?: string | null
  description_ur?: string | null
  price?: number | string | null
  offer_price?: number | string | null
  currency?: string | null
  total_sessions?: number | null
  is_pickup_included?: boolean
  tabby_enabled?: boolean
  sort_order?: number | null
  is_active: boolean
  items?: AllInOneItem[]
  [k: string]: unknown
}

export interface AllInOneBundle {
  id: number
  title: string
  title_ar?: string | null
  title_ur?: string | null
  subtitle?: string | null
  subtitle_ar?: string | null
  subtitle_ur?: string | null
  badge?: string | null
  badge_ar?: string | null
  badge_ur?: string | null
  highlights?: string[] | null
  highlights_ar?: string[] | null
  highlights_ur?: string[] | null
  image_url?: string | null
  city_id?: number | null
  sort_order?: number | null
  is_active: boolean
  variants?: AllInOneVariant[]
  created_at?: string | null
  [k: string]: unknown
}

// Per-student progress on one item of the variant they enrolled in — separate
// from AllInOneItem (the reusable template row), since this one carries the
// student's own status/scheduling/session-count state.
export interface AllInOneEnrollmentItem {
  id: number
  enrollment_id: number
  variant_item_id: number
  code: string
  title: string
  title_ar?: string | null
  title_ur?: string | null
  icon?: string | null
  item_type: string
  sort_order?: number | null
  status: number
  scheduled_at?: string | null
  started_at?: string | null
  completed_at?: string | null
  sessions_total?: number | null
  sessions_done?: number | null
  result?: string | null
  location?: string | null
  note?: string | null
  updated_by?: number | null
  created_at?: string
  updated_at?: string
  [k: string]: unknown
}

export interface AllInOneEnrollment {
  id: number
  user_id: number
  bundle_id: number
  variant_id: number
  trainer_id?: number | null
  city_id?: number | null
  status: number
  status_label?: string
  payment_status?: number
  payment_method?: number
  amount?: number
  amount_paid?: number
  currency?: string
  external_payment_ref?: string | null
  transaction_id?: number | null
  // Frozen copy of the variant (with its items) as it was at purchase time —
  // used to show what the student actually bought even if the live template
  // (AllInOneVariant below) changes later.
  snapshot?: AllInOneVariant
  variant?: AllInOneVariant
  items?: AllInOneEnrollmentItem[]
  progress?: { done: number; total: number; percent: number }
  student_name?: string
  student_phone?: string | null
  student_photo_url?: string | null
  booking_id?: number | null
  // Only present when a trainer is assigned — absent (not null) otherwise, so check trainer_id for "is assigned".
  trainer?: { id: number; name?: string } | null
  paid_at?: string | null
  completed_at?: string | null
  cancelled_at?: string | null
  created_at?: string
  updated_at?: string
  [k: string]: unknown
}

export const allInOneService = {
  // GET /bundles — every bundle (incl. inactive variants) with its variants and items nested.
  listBundles: (): Promise<{ data: AllInOneBundle[] }> =>
    restClient.get(endpoints.allInOne.bundles).then(r => r.data),

  // POST /bundle/save — no id: creates a bundle. Used only for create here;
  // edits go through PUT /bundle/:id below (the dedicated edit endpoint).
  saveBundle: (data: Record<string, unknown>) =>
    restClient.post(endpoints.allInOne.bundleSave, data).then(r => r.data),
  // PUT /bundle/:id — edits a bundle (partial).
  updateBundle: (id: number, data: Record<string, unknown>) =>
    restClient.put(endpoints.allInOne.bundleById(id), data).then(r => r.data),
  deleteBundle: (id: number) =>
    restClient.delete(endpoints.allInOne.bundleById(id)).then(r => r.data),

  // POST /variant/save — create needs bundle_id, code, title, price.
  saveVariant: (data: Record<string, unknown>) =>
    restClient.post(endpoints.allInOne.variantSave, data).then(r => r.data),
  deleteVariant: (id: number) =>
    restClient.delete(endpoints.allInOne.variantById(id)).then(r => r.data),

  // POST /item/save — create needs variant_id, code, title.
  saveItem: (data: Record<string, unknown>) =>
    restClient.post(endpoints.allInOne.itemSave, data).then(r => r.data),
  deleteItem: (id: number) =>
    restClient.delete(endpoints.allInOne.itemById(id)).then(r => r.data),

  // Student enrollments — separate from the bundle/variant/item template builder above.
  listEnrollments: (p: { status?: string; user_id?: number; trainer_id?: number; city_id?: number; page?: number; limit?: number } = {}): Promise<{
    status: string; message: string; total: number; count_per_page: number; data: AllInOneEnrollment[]
  }> => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 15 }
    if (p.status) params.status = p.status
    if (p.user_id) params.user_id = p.user_id
    if (p.trainer_id) params.trainer_id = p.trainer_id
    if (p.city_id) params.city_id = p.city_id
    return restClient.get(`${endpoints.allInOne.enrollments}?${q(params)}`).then(r => r.data)
  },
  // "Paid" = active + completed. The API takes one status per call, so fetch
  // every page of both and merge (newest first); the caller paginates the result.
  listPaidEnrollments: async (p: { user_id?: number } = {}): Promise<AllInOneEnrollment[]> => {
    const all: AllInOneEnrollment[] = []
    for (const status of ['1', '2']) {
      for (let page = 1; page <= 50; page++) {
        const res = await allInOneService.listEnrollments({ status, user_id: p.user_id, page, limit: 100 })
        const rows = res.data ?? []
        all.push(...rows)
        if (rows.length === 0 || all.length >= 5000 || page * (res.count_per_page || 100) >= res.total) break
      }
    }
    return all.sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))
  },
  getEnrollment: (id: number): Promise<{ data: AllInOneEnrollment }> =>
    restClient.get(endpoints.allInOne.enrollmentById(id)).then(r => r.data),
  assignTrainer: (id: number, data: { trainer_id: number; package_id: number; note?: string }) =>
    restClient.post(endpoints.allInOne.enrollmentAssignTrainer(id), data).then(r => r.data),
  cancelEnrollment: (id: number, data: { note?: string } = {}) =>
    restClient.post(endpoints.allInOne.enrollmentCancel(id), data).then(r => r.data),
  markPaid: (id: number, data: { amount_paid?: number; reference?: string; note?: string } = {}) =>
    restClient.post(endpoints.allInOne.enrollmentMarkPaid(id), data).then(r => r.data),
  // status: numeric enrollment-item status code.
  updateEnrollmentItemStatus: (id: number, data: {
    status: number; scheduled_at?: string | null; location?: string; result?: string; note?: string; payload?: Record<string, unknown>
  }) =>
    restClient.post(endpoints.allInOne.enrollmentItemStatus(id), data).then(r => r.data),
}
